import { Link, useLocation } from "@tanstack/react-router";

import { Suspense, lazy, useState } from "react";
import { ErrorBoundary } from "@sentry/react";
import { useMediaQuery } from "@uidotdev/usehooks";
import { ListVideoIcon, MenuIcon, UserCircle2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import { Button } from "./ui/button";
import FooterLinks from "./FooterLinks";
import LanguageSelector from "./LanguageSelector";
import { useOptionalTracking } from "./ClientAnalytics";
import { useIsLoggedIn } from "@/stores/useAuth";
import { cn } from "@/lib/utils";
import { useAppStateStore } from "@/stores/useAppState";

// Lazy, and only mounted from `lg`: a lazy component fetches its chunk when it
// renders, CSS-hidden or not, and Radix's menu (~20 kB gzipped) has no use on
// a phone. The header mounts on every page, the player included.
const GenresMenu = lazy(
  () => import("@/modules/Catalog/components/GenresMenu"),
);

// The nav bar's links (SHO-864). TanStack marks the current one with
// `data-status="active"`; the Genres trigger mirrors it with `data-active`.
const NAV_LINK_CLASS =
  "text-neutral-20 hover:text-white data-[status=active]:text-white data-[active=true]:text-white p-2 text-sm font-semibold transition-colors";

// Two layouts in one header: the burger below the `lg` breakpoint, the nav
// bar (All series / Genres / My list, Get the app) from it. The burger keeps
// what it always held; the nav bar is the Figma `nav` frame.
const Header: React.FC<React.ComponentProps<"header">> = ({
  className,
  ...props
}) => {
  const { pathname } = useLocation();

  const isLoggedIn = useIsLoggedIn();

  const openLoginModal = useAppStateStore((s) => s.openLoginModal);
  // Nullable: the root error screen renders this header outside the provider.
  const tracking = useOptionalTracking();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  // Tailwind's `lg`, the nav bar's own breakpoint.
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  // The Genres trigger, inert: below `lg` (nothing to open), while the menu's
  // chunk loads, and if it fails. The boundary keeps that failure to the menu:
  // `GlobalError` renders this header too, so an uncaught chunk error would
  // throw again inside the error screen.
  const inertGenresTrigger = (
    <button type="button" disabled className={NAV_LINK_CLASS}>
      {t("header_genres_menu_trigger")}
    </button>
  );

  return (
    <header
      className={cn("bg-neutral-70 text-white shadow-lg", className)}
      {...props}
    >
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-2 p-6">
        <div className="flex items-center gap-10">
          <h1 className="text-xl font-semibold">
            <Link to="/">
              <img
                src="/Logo.webp"
                alt={t("header_logo_alt")}
                className="h-10 w-auto"
                width="122"
                height="40"
              />
            </Link>
          </h1>
          <nav className="hidden items-center gap-6 lg:flex">
            <Link
              to="/series"
              // Exact: TanStack's default would also light it on every
              // `/series/{show}` details page.
              activeOptions={{ exact: true }}
              data-test-id="header-nav-all-series"
              className={NAV_LINK_CLASS}
            >
              {t("header_all_series_link")}
            </Link>
            {isDesktop ? (
              <ErrorBoundary fallback={inertGenresTrigger}>
                <Suspense fallback={inertGenresTrigger}>
                  <GenresMenu className={NAV_LINK_CLASS} />
                </Suspense>
              </ErrorBoundary>
            ) : (
              inertGenresTrigger
            )}
            <Link
              to="/my-list"
              data-test-id="header-nav-my-list"
              className={NAV_LINK_CLASS}
            >
              {t("header_my_list_link")}
            </Link>
          </nav>
        </div>
        <div className="flex items-center gap-2 lg:gap-3">
          <Button
            asChild
            size="sm"
            // A white link next to the Sign in button for a guest, so the
            // header holds a single primary action: the nav links' hover
            // inverted (white at rest, `neutral-20` on hover, no underline).
            // The primary button once signed in, where only the profile icon
            // sits next to it. A Button either way, so its height matches
            // Sign in's.
            variant={isLoggedIn ? "default" : "link"}
            className={cn(
              "hidden lg:inline-flex",
              !isLoggedIn &&
                "hover:text-neutral-20 text-white hover:no-underline",
            )}
          >
            <Link
              to="/get-app"
              data-test-id="header-get-app"
              onClick={() => tracking?.trackAppDownloadClick("web_header")}
            >
              {t("header_get_app_button")}
            </Link>
          </Button>
          {isLoggedIn ? (
            <>
              {pathname !== "/profile" && (
                <Link
                  to="/profile"
                  className="rounded-lg p-2 transition-colors hover:bg-gray-700"
                  aria-label={t("header_profile_open_aria")}
                >
                  <UserCircle2Icon size={24} />
                </Link>
              )}
            </>
          ) : (
            <Button
              className="min-w-24"
              size="sm"
              onClick={() => openLoginModal({ origin: "header" })}
            >
              {t("auth_signin_button")}
            </Button>
          )}

          <Sheet onOpenChange={setIsOpen} open={isOpen}>
            <SheetTrigger asChild>
              <button
                data-test-id="header-menu-trigger"
                className="rounded-lg p-2 transition-colors hover:bg-gray-700 lg:hidden"
                aria-label={t("header_menu_trigger_aria")}
              >
                <MenuIcon size={24} />
              </button>
            </SheetTrigger>
            <SheetContent data-test-id="header-menu">
              <SheetHeader>
                <SheetTitle className="sr-only">
                  {t("header_menu_title")}
                </SheetTitle>
              </SheetHeader>
              <div className="overflow-y-auto px-6 pb-10">
                <Link
                  to="/my-list"
                  data-test-id="header-menu-my-list-link"
                  className="mb-6 flex items-center gap-2 text-white"
                  onClick={() => setIsOpen(false)}
                >
                  <ListVideoIcon size={20} />
                  {t("header_my_list_link")}
                </Link>
                <LanguageSelector
                  titleKey="menu_language_header"
                  onSelect={() => setIsOpen(false)}
                />
                <FooterLinks stacked onNavigate={() => setIsOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
